/**
 * Scene writing API (#857). Compose one instruction. Resolve fights.
 * Generate from that one text. Never two ranked prompts.
 */
import { api, LONG_REQUEST_TIMEOUT_MS } from './client';
import { sceneAddress } from '../utils/sceneWritingPath';
import { notifyNoteChanged } from '../utils/noteChanged';

const unwrap = (res) => (res?.data?.data ?? res?.data ?? res);

export async function composeSceneWriting(storyId, { sceneId, sceneLabel, openNext = false, signal } = {}) {
  const params = new URLSearchParams();
  const address = sceneAddress(sceneLabel || sceneId);
  if (address.label) params.set('scene_label', address.label);
  else if (address.id) params.set('scene_id', address.id);
  if (openNext) params.set('open_next', 'true');
  const q = params.toString() ? `?${params}` : '';
  // no-store: returning from Book Notes must not reuse a compose from before the edit.
  return unwrap(await api.get(`/stories/${storyId}/scene-writing${q}`, {
    cache: 'no-store',
    ...(signal ? { signal } : {}),
  }));
}

export async function resolveSceneWritingFight(storyId, body) {
  return unwrap(await api.post(`/stories/${storyId}/scene-writing/resolve`, {
    scene_id: body.sceneId || body.scene_id,
    fight_id: body.fightId || body.fight_id,
    choice: body.choice,
    edited_instruction: body.editedInstruction || body.edited_instruction || null,
    resolutions: body.resolutions || [],
  }));
}

export async function estimateSceneWriting(storyId, body) {
  return unwrap(await api.post(`/stories/${storyId}/scene-writing/estimate`, {
    scene_id: body.sceneId || body.scene_id,
    merged_instruction: body.mergedInstruction || body.merged_instruction || '',
    resolutions: body.resolutions || [],
  }));
}

export async function generateSceneWriting(storyId, body) {
  const modelId = (body.modelId ?? body.model_id ?? '').toString().trim();
  const payload = {
    scene_id: body.sceneId || body.scene_id,
    merged_instruction: body.mergedInstruction || body.merged_instruction || '',
    resolutions: body.resolutions || [],
  };
  if (modelId) payload.model_id = modelId;
  const genders = Array.isArray(body.genders) ? body.genders : [];
  const chosen = genders
    .map((row) => ({
      note_id: row.noteId || row.note_id || null,
      name: row.name || '',
      gender: row.gender,
    }))
    .filter((row) => row.name && row.gender);
  if (chosen.length) payload.genders = chosen;
  return unwrap(await api.post(`/stories/${storyId}/scene-writing/generate`, payload));
}

export async function saveSceneWritingDraft(storyId, body, { keepalive = false } = {}) {
  const payload = {
    scene_id: body.sceneId || body.scene_id,
    pass_id: body.passId || body.pass_id,
    generated_text: body.generatedText ?? body.generated_text ?? '',
  };
  if (body.confirmShrink || body.confirm_shrink) payload.confirm_shrink = true;
  const url = `/stories/${storyId}/scene-writing/draft`;
  if (keepalive) return unwrap(await api.post(url, payload, { keepalive: true }));
  return unwrap(await api.post(url, payload));
}

export async function restoreAcceptedScene(storyId, body) {
  return unwrap(await api.post(`/stories/${storyId}/scene-writing/restore-accepted`, {
    scene_id: body.sceneId || body.scene_id,
    pass_id: body.passId || body.pass_id,
  }));
}

export async function undoAcceptSceneWriting(storyId, body) {
  return unwrap(await api.post(`/stories/${storyId}/scene-writing/undo-accept`, {
    scene_id: body.sceneId || body.scene_id,
    pass_id: body.passId || body.pass_id || null,
  }));
}

export async function acceptSceneWriting(storyId, body) {
  const revised = body.generatedText ?? body.generated_text;
  return unwrap(await api.post(`/stories/${storyId}/scene-writing/accept`, {
    scene_id: body.sceneId || body.scene_id,
    pass_id: body.passId || body.pass_id || null,
    generated_text: revised === undefined ? null : revised,
  }));
}

export async function estimateContinueSceneWriting(storyId, body) {
  const revised = body.generatedText ?? body.generated_text;
  return unwrap(await api.post(`/stories/${storyId}/scene-writing/continue/estimate`, {
    scene_id: body.sceneId || body.scene_id,
    pass_id: body.passId || body.pass_id || null,
    generated_text: revised === undefined ? null : revised,
  }));
}

export async function continueSceneWriting(storyId, body) {
  const revised = body.generatedText ?? body.generated_text;
  return unwrap(await api.post(`/stories/${storyId}/scene-writing/continue`, {
    scene_id: body.sceneId || body.scene_id,
    pass_id: body.passId || body.pass_id || null,
    generated_text: revised === undefined ? null : revised,
  }));
}

function passageRewritePayload(body = {}) {
  const instruction = (body.instruction ?? '').toString().trim();
  const modelId = (body.modelId ?? body.model_id ?? '').toString().trim();
  const lengthRaw = body.length;
  const length = lengthRaw === 'shorter' || lengthRaw === 'longer' ? lengthRaw : 'same';
  const notes = body.useNotesAndStyle ?? body.use_notes_and_style;
  return {
    selection_start: Number(body.selectionStart ?? body.selection_start ?? 0),
    selection_end: Number(body.selectionEnd ?? body.selection_end ?? 0),
    selected_text: body.selectedText ?? body.selected_text ?? '',
    instruction: instruction || null,
    length,
    model_id: modelId || null,
    use_notes_and_style: notes === undefined ? true : Boolean(notes),
  };
}

/**
 * Passage estimate (#2673). The client prefixes /api.
 * 200: prompt_chars, estimated_input_tokens, output_budget (the call stop,
 * not the shown band), typical_output_tokens, worst_output_tokens,
 * calls, note_offers_enabled. No draft_check and no note_facts.
 * 409 detail.code passage_changed: the saved prose does not match.
 */
export async function estimatePassageRewrite(storyId, sceneId, body) {
  const scene = encodeURIComponent(sceneId);
  return unwrap(await api.post(
    `/stories/${storyId}/scene-writing/${scene}/passage-rewrite/estimate`,
    passageRewritePayload(body),
  ));
}

/**
 * A new version of the selected passage only (#2673). The route saves nothing.
 * 200: { text, model_id, word_count, selection_start, selection_end, selected_text }.
 * text replaces the response span (the server widens a part-word selection).
 * 409 detail.code passage_changed: the saved prose moved. Nothing was generated.
 * 502 detail.code rewrite_failed: nothing was saved.
 * 402 is the same insufficient_credits error as scene drafts.
 */
export async function rewritePassage(storyId, sceneId, body) {
  const scene = encodeURIComponent(sceneId);
  return unwrap(await api.post(
    `/stories/${storyId}/scene-writing/${scene}/passage-rewrite`,
    passageRewritePayload(body),
    { timeoutMs: LONG_REQUEST_TIMEOUT_MS },
  ));
}

export async function resolveSceneNoteOffer(storyId, offerId, body) {
  const result = unwrap(await api.post(
    `/stories/${storyId}/scene-writing/note-offers/${offerId}`,
    {
      action: body.action,
      edited_text: body.editedText || body.edited_text || null,
    },
  ));
  const action = String(body?.action || '').trim().toLowerCase();
  if (action === 'accept' || action === 'edit') notifyNoteChanged(storyId);
  return result;
}

// Migration 183: drafts whose scene was deleted.
export async function listDetachedDrafts(storyId) {
  return unwrap(await api.get(`/stories/${storyId}/scene-writing/detached`));
}

export async function readDetachedDraft(storyId, passId) {
  return unwrap(await api.get(`/stories/${storyId}/scene-writing/detached/${passId}`));
}

export async function attachDetachedDraft(storyId, passId, sceneId) {
  return unwrap(await api.post(
    `/stories/${storyId}/scene-writing/detached/${passId}/attach`,
    { scene_id: sceneId },
  ));
}

export const sceneWritingApi = {
  compose: composeSceneWriting,
  resolve: resolveSceneWritingFight,
  estimate: estimateSceneWriting,
  estimateContinue: estimateContinueSceneWriting,
  generate: generateSceneWriting,
  saveDraft: saveSceneWritingDraft,
  restoreAccepted: restoreAcceptedScene,
  accept: acceptSceneWriting,
  undoAccept: undoAcceptSceneWriting,
  continueDraft: continueSceneWriting,
  resolveNoteOffer: resolveSceneNoteOffer,
  estimatePassageRewrite,
  rewritePassage,
};
